import type { SxProps, Theme } from "@mui/material";
import { DOCUMENT_REJECTION_REASONS, type StoredMediaFile } from "@repo/types";
import type { ReactNode } from "react";
import type { FieldError } from "react-hook-form";

// Base document interface (upload UI; forms may omit optional fields)
export type Document = StoredMediaFile;

// Upload metadata for file operations
export type UploadMetadata = {
  folder: string;
  subFolder: string;
  driverId?: string | number;
  docId?: string | number;
};

// Root component props
export type UploadRootProps = {
  children: ReactNode;
  sx?: SxProps<Theme>;
};

// Item component props
export type UploadItemProps = {
  value: Document;
  meta: UploadMetadata;
  canEdit: boolean;
  error?: FieldError;
  handleDelete?: (key: string) => void;
  handleFileUploaded?: (file: Document) => void;
  /** The delivery card never shows review status; only driver documents do. */
  showStatus?: boolean;
  /**
   * Append " - AG" to the title when the document is system-generated
   * (`origin === "SYSTEM"`), so it can be told apart from a same-named upload.
   */
  showGeneratedSuffix?: boolean;
};

// Review item props
export type ReviewItemProps = {
  value: Document;
  meta: UploadMetadata;
  handleDelete?: (key: string) => void;
  handleFileUploaded?: (file: Document) => void;
};

// Preview component props
export type UploadPreviewProps = {
  open: boolean;
  onClose: () => void;
  document: Document;
  isImage: boolean;
};

// Progress component props
export type UploadProgressProps = {
  value: number;
  loading?: boolean;
};

// Actions component props
export type UploadActionsProps = {
  id?: string;
  onOpen?: () => void;
  onChange?: () => void;
  canOpen?: boolean;
  canDownload?: boolean;
  canChange?: boolean;
  loading?: boolean;
  onDelete?: () => void;
  canDelete?: boolean;
  onDownload?: () => void;
  readyOnly?: boolean;
};

// Content component props
export type UploadContentProps = {
  children: ReactNode;
  loading?: boolean;
  isDragActive?: boolean;
  isDragDropEnabled?: boolean;
  hasDocument?: boolean;
  hasPendingReview?: boolean;
};

export const documentCancellationReasonKeys = DOCUMENT_REJECTION_REASONS;
